import { Text } from '@cotera/client/app/components';
import { classNames } from '@cotera/client/app/components/utils';
import { CATEGORICAL_TAGS } from '@cotera/sdk/core';
import React, { useEffect, useRef, useState } from 'react';
import { StatsProps } from './types';
import { valueToColor } from './utils';
import { SvgBar } from './components';

export const Categorical: React.FC<Omit<StatsProps, 'type' | 'buckets'>> = ({
  categories,
  numDistinct,
  onClick,
  active,
  horizontal = false,
}) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const total =
    categories.reduce((acc, curr) => acc + curr.count, 0) + numDistinct;

  const uniquePercent = (numDistinct / total) * 100;
  const categoryCountsTotal =
    categories.reduce((acc, curr) => acc + curr.count, 0) + numDistinct;

  return (
    <div className="flex flex-col h-full w-full">
      <div
        ref={containerRef}
        className={classNames(
          'flex flex-row h-full w-full rounded overflow-hidden',
          horizontal ? '' : 'mb-1'
        )}
      >
        <SvgCategoricalChart
          active={active}
          uniquePercent={uniquePercent}
          categoryCountsTotal={categoryCountsTotal}
          categories={categories}
          onClick={onClick}
          numDistinct={numDistinct}
          height={26}
          width={containerWidth}
        />
      </div>
      {!horizontal && (
        <Text.Caption
          className={classNames(
            'flex flew-row text-xs',
            horizontal ? '' : 'mb-1'
          )}
        >
          {numDistinct} Categor{numDistinct === 1 ? 'y' : 'ies'}
        </Text.Caption>
      )}
    </div>
  );
};

const SvgCategoricalChart: React.FC<
  Omit<StatsProps, 'type' | 'buckets'> & {
    uniquePercent: number;
    categoryCountsTotal: number;
    width: number;
    height: number;
  }
> = ({
  categories,
  numDistinct,
  onClick,
  active,
  uniquePercent,
  categoryCountsTotal,
  width,
  height,
}) => {
  let currentX = 0;
  const gap = 1; // 1px gap
  const minBarWidth = 3;

  const numBars = categories.length + (numDistinct > 0 ? 1 : 0);
  const totalGapsWidth = numBars > 1 ? (numBars - 1) * gap : 0;
  const availableWidth = Math.max(0, width - totalGapsWidth);

  // Calculate natural widths
  const barDefs = categories.map((category) => {
    const categoryPercent = (category.count / categoryCountsTotal) * 100;
    return {
      value: category.value,
      count: category.count,
      percent: categoryPercent,
      naturalWidth: (categoryPercent / 100) * availableWidth,
    };
  });
  if (numDistinct > 0) {
    barDefs.push({
      value: 'unique',
      count: numDistinct,
      percent: uniquePercent,
      naturalWidth: (uniquePercent / 100) * availableWidth,
    });
  }

  // First pass: clamp bars to minBarWidth, sum clamped widths
  let clampedWidths = barDefs.map((bar) =>
    Math.max(bar.naturalWidth, minBarWidth)
  );
  let totalClampedWidth = clampedWidths.reduce((acc, w) => acc + w, 0);

  // If totalClampedWidth > availableWidth, scale down proportionally (but never below minBarWidth)
  if (totalClampedWidth > availableWidth && availableWidth > 0) {
    // Calculate the total width above minBarWidth
    const totalAboveMin = clampedWidths.reduce(
      (acc, w) => acc + (w > minBarWidth ? w - minBarWidth : 0),
      0
    );
    const excess = totalClampedWidth - availableWidth;
    clampedWidths = clampedWidths.map((w) => {
      if (w > minBarWidth && totalAboveMin > 0) {
        // Reduce proportionally
        const reduction = ((w - minBarWidth) / totalAboveMin) * excess;
        return Math.max(minBarWidth, w - reduction);
      }
      return w;
    });
    // Recalculate total (should now be close to availableWidth)
    totalClampedWidth = clampedWidths.reduce((acc, w) => acc + w, 0);
  }

  return (
    <svg width={width} height={height} className="block">
      {/* Define patterns for null values */}
      <defs>
        <pattern
          id="nullStripes"
          patternUnits="userSpaceOnUse"
          width="6"
          height="6"
          patternTransform="rotate(45)"
        >
          <rect width="8" height="8" fill="#FEE9A3" />
          <rect width="4" height="8" fill="#FFF9D0" />
        </pattern>
        <pattern
          id="nullStripesActive"
          patternUnits="userSpaceOnUse"
          width="6"
          height="6"
          patternTransform="rotate(45)"
        >
          <rect width="8" height="8" fill="#ccaa00" />
          <rect width="4" height="8" fill="#e6c200" />
        </pattern>
        <pattern
          id="nullStripesHover"
          patternUnits="userSpaceOnUse"
          width="6"
          height="6"
          patternTransform="rotate(45)"
        >
          <rect width="8" height="8" fill="#ccaa00" />
          <rect width="4" height="8" fill="#e6c200" />
        </pattern>
      </defs>

      {barDefs.map((bar, i) => {
        const barWidth =
          typeof clampedWidths[i] === 'number' ? clampedWidths[i] : minBarWidth;
        const color = valueToColor(
          bar.value === 'unique'
            ? 'unique'
            : active?.includes(bar.value ?? 'null')
            ? 'active'
            : bar.value,
          bar.value
        );
        const isClickable =
          bar.value !== 'unique' &&
          ![CATEGORICAL_TAGS.discrete].includes(bar.value ?? '');
        const rect = (
          <SvgBar
            isActive={
              bar.value !== 'unique' && (active?.includes(bar.value) ?? false)
            }
            key={bar.value ?? i}
            x={currentX}
            y={0}
            width={barWidth}
            height={height}
            color={color}
            category={bar.value}
            rows={bar.count}
            percent={bar.percent}
            onClick={isClickable ? onClick : undefined}
          />
        );
        currentX += barWidth + gap;
        return rect;
      })}
    </svg>
  );
};
